import { Container } from '@/components';
import { BlogCard } from '@/components/Blog';
import {
  BlogDescription,
  BlogGrid,
  BlogTitle,
  BlogWrapper,
  NoPostsText,
  TagChip,
  TagChipText,
  TagFilterList,
} from '@/components/Blog/Blog.styles';
import Pagination from '@/components/Pagination';
import { BLOG_COPY, BLOG_PAGE_SIZE, BLOG_TAGS, getBlogTagLabel } from '@/constants';
import { getBlogPosts } from '@/fetchers';
import { isBlogTag } from '@/fetchers/contentfulAPI/blogPost.fetchers';
import { useRouteChangePending } from '@/hooks';
import type { IBlogPostCard } from '@/interfaces';
import { buildBlogPath, checkIsPreviewModeRequest, parsePageParam, setPublicPageCacheHeader } from '@/utils';
import type { GetServerSideProps, InferGetServerSidePropsType, NextPage } from 'next';
import { NextSeo } from 'next-seo';
import getConfig from 'next/config';

const { publicRuntimeConfig } = getConfig();

interface IBlogListServerSideProps {
  posts: IBlogPostCard[];
  currentPage: number;
  totalPages: number;
  tag: string | null;
}

const BlogListPage: NextPage<InferGetServerSidePropsType<typeof getServerSideProps>> = ({
  posts,
  currentPage,
  totalPages,
  tag,
}) => {
  const isNavigating = useRouteChangePending();

  const canonicalUrl = `${publicRuntimeConfig.origin}${buildBlogPath({ page: currentPage, tag })}`;
  const metaTitle = `${BLOG_COPY.title} | Parcel Monitor`;

  // ?page=N stays indexable — those pages carry the links to every article. ?tag=X does
  // not: with a handful of posts each tag page is a near-copy of the hub, and duplicate
  // listings are the thin content the site is being penalised for. `follow` keeps the
  // article links on them crawlable.
  const isTagFiltered = !!tag;

  return (
    <>
      <NextSeo
        title={metaTitle}
        description={BLOG_COPY.description}
        canonical={canonicalUrl}
        noindex={isTagFiltered}
        openGraph={{
          url: canonicalUrl,
          title: metaTitle,
          description: BLOG_COPY.description,
          images: [{ url: `${publicRuntimeConfig.origin}/images/parcel_monitor_og_image.png` }],
          type: 'website',
        }}
      />
      <Container>
        <BlogWrapper>
          <BlogTitle>{BLOG_COPY.title}</BlogTitle>
          <BlogDescription>{BLOG_COPY.description}</BlogDescription>

          <TagFilterList>
            <li>
              <TagChip href={buildBlogPath({})} isActive={!tag}>
                <TagChipText>{BLOG_COPY.allTags}</TagChipText>
              </TagChip>
            </li>
            {BLOG_TAGS.map((blogTag) => (
              <li key={blogTag}>
                <TagChip href={buildBlogPath({ tag: blogTag })} isActive={tag === blogTag}>
                  <TagChipText>{getBlogTagLabel(blogTag)}</TagChipText>
                </TagChip>
              </li>
            ))}
          </TagFilterList>

          {posts.length > 0 ? (
            <BlogGrid isPending={isNavigating}>
              {posts.map((post) => (
                <BlogCard key={post.slug} post={post} />
              ))}
            </BlogGrid>
          ) : (
            <NoPostsText>{BLOG_COPY.noPosts}</NoPostsText>
          )}

          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            buildPageHref={(page) => buildBlogPath({ page, tag })}
          />
        </BlogWrapper>
      </Container>
    </>
  );
};

export const getServerSideProps: GetServerSideProps<IBlogListServerSideProps> = async ({ query, res }) => {
  const page = parsePageParam(query.page);
  if (page === null) return { notFound: true };

  const rawTag = Array.isArray(query.tag) ? undefined : query.tag;
  if (rawTag && !isBlogTag(rawTag)) return { notFound: true };
  const tag = rawTag ?? null;

  const isPreviewMode = checkIsPreviewModeRequest(query);
  const result = await getBlogPosts({ page, tag: tag ?? undefined, isPreviewMode });

  // A Contentful outage must surface as a 5xx, never a cacheable empty page or 404.
  if (!result) {
    throw new Error('getBlogPosts returned no data');
  }

  const totalPages = Math.max(1, Math.ceil(result.total / BLOG_PAGE_SIZE));
  if (page > totalPages) return { notFound: true };

  setPublicPageCacheHeader(res, isPreviewMode);

  return {
    props: {
      posts: result.items,
      currentPage: page,
      totalPages,
      tag,
    },
  };
};

export default BlogListPage;
